import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import UnsubscribeOutlinedIcon from '@mui/icons-material/UnsubscribeOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import logoFull from '../../assets/images/logo-full.png';
import { SetChangelogEmailSubscriptionAPI } from '../../api/PublicChangelogAPI';
import usePageMeta from './usePageMeta';
import styles from './PublicChangelog.module.css';

const OPERATIONAL_HINT = 'Avisos da sua conta, cobrança e segurança continuam chegando.';

// Textos por assunto: ?assunto=dicas vem do rodapé dos e-mails de acompanhamento
const TOPICS = {
  changelog: {
    meta: ['Novidades por e-mail | Gabriela', 'Preferência de e-mail das Novidades da Gabriela.'],
    linkName: 'Não quero mais receber novidades',
    off: 'Pronto, você não vai mais receber as novidades da Gabriela por e-mail',
    on: 'Pronto, você volta a receber as novidades da Gabriela por e-mail',
  },
  onboarding: {
    meta: ['Dicas de uso por e-mail | Gabriela', 'Preferência de e-mail das dicas de uso da Gabriela.'],
    linkName: 'Não quero mais receber dicas de uso',
    off: 'Pronto, você não vai mais receber as dicas de uso da Gabriela por e-mail',
    on: 'Pronto, você volta a receber as dicas de uso da Gabriela por e-mail',
  },
};

/**
 * /novidades/descadastrar?t=<token>: descadastro em um clique, sem login.
 * Com &assunto=dicas, vale para as dicas de uso (e-mails de acompanhamento).
 * Ao abrir já descadastra; "Desfazer" volta a receber.
 *
 * Estados: loading | unsubscribed | resubscribed | invalid (sem token) | error
 */
const PublicChangelogUnsubscribePage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t') || '';
  const topic = searchParams.get('assunto') === 'dicas' ? 'onboarding' : 'changelog';
  const texts = TOPICS[topic];
  const [status, setStatus] = useState(token ? 'loading' : 'invalid');
  const [emailHint, setEmailHint] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  usePageMeta(texts.meta[0], texts.meta[1]);

  const apply = useCallback((action) => {
    setSaving(true);
    SetChangelogEmailSubscriptionAPI(token, action, (response) => {
      setSaving(false);
      if (!response || response.success !== true) {
        setError(response?.error || 'Não foi possível concluir agora. Tente de novo em instantes.');
        setStatus('error');
        return;
      }
      if (response.email_hint) setEmailHint(response.email_hint);
      setError('');
      setStatus(response.subscribed ? 'resubscribed' : 'unsubscribed');
    }, topic);
  }, [token, topic]);

  useEffect(() => {
    if (token) apply('unsubscribe');
  }, [token, apply]);

  const renderContent = () => {
    if (status === 'loading') {
      return (
        <>
          <CircularProgress size={32} />
          <p className={styles.unsubscribeText}>Atualizando sua preferência...</p>
        </>
      );
    }

    if (status === 'invalid' || status === 'error') {
      return (
        <>
          <span className={`${styles.unsubscribeIcon} ${styles.unsubscribeIconError}`}>
            <ErrorOutlineIcon />
          </span>
          <h1 className={styles.unsubscribeTitle}>
            {status === 'invalid' ? 'Este link não é válido' : 'Não deu certo desta vez'}
          </h1>
          <p className={styles.unsubscribeText}>
            {status === 'invalid'
              ? `Abra de novo o link "${texts.linkName}" do e-mail. Se preferir, entre na Gabriela e desligue em Meu Perfil, no bloco Comunicações.`
              : error}
          </p>
          <div className={styles.unsubscribeActions}>
            {status === 'error' && (
              <button type="button" className={styles.ctaButton} onClick={() => apply('unsubscribe')} disabled={saving}>
                Tentar de novo
              </button>
            )}
            <Link className={styles.link} to="/profile">Abrir Meu Perfil</Link>
          </div>
        </>
      );
    }

    const unsubscribed = status === 'unsubscribed';
    return (
      <>
        <span className={styles.unsubscribeIcon}>
          {unsubscribed ? <UnsubscribeOutlinedIcon /> : <MarkEmailReadOutlinedIcon />}
        </span>
        <h1 className={styles.unsubscribeTitle}>
          {unsubscribed ? texts.off : texts.on}
        </h1>
        {emailHint && <p className={styles.unsubscribeText}>E-mail: <strong>{emailHint}</strong></p>}
        <p className={styles.unsubscribeHint}>{OPERATIONAL_HINT}</p>
        <div className={styles.unsubscribeActions}>
          {unsubscribed ? (
            <button type="button" className={styles.secondaryButton} onClick={() => apply('resubscribe')} disabled={saving}>
              {saving ? 'Desfazendo...' : 'Desfazer'}
            </button>
          ) : (
            <button type="button" className={styles.secondaryButton} onClick={() => apply('unsubscribe')} disabled={saving}>
              {saving ? 'Salvando...' : 'Não quero mais receber'}
            </button>
          )}
        </div>
        <Link className={styles.link} to="/novidades">Ver as novidades no site</Link>
      </>
    );
  };

  return (
    <div className={styles.unsubscribePage}>
      <div className={styles.unsubscribeCard} aria-live="polite">
        <img src={logoFull} alt="Gabriela" className={styles.unsubscribeLogo} />
        {renderContent()}
      </div>
    </div>
  );
};

export default PublicChangelogUnsubscribePage;
